"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import Tabs, { TAB_ORIENTATION, TAB_STYLE, TabItem } from "@/components/common/Tab";
import SubpageBackground from "@/components/common/SubpageBackground";
import PageTitle from "@/components/common/PageTitle";

export default function ServiceLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  const tabs: TabItem[] = [
    {
      id: "scenario-based",
      name: "Scenario-based Penetration Test",
      value: "/service/offend/scenario-based-penetration-test",
      href: "/service/offend/scenario-based-penetration-test",
    },
    {
      id: "adversary-simulation",
      name: "Adversary Simulation",
      value: "/service/offend/adversary-simulation",
      href: "/service/offend/adversary-simulation",
    },
  ];

  // Assessment tab group — Web, Mobile, and Compromise Assessment
  // (Compromise Assessment moved here from Defend per Figma node 2233:52583).
  const tabsAssessment: TabItem[] = [
    {
      id: "web-application",
      name: "Web Application",
      value: "/service/offend/web-application",
      href: "/service/offend/web-application",
    },
    {
      id: "mobile-application",
      name: "Mobile Application",
      value: "/service/offend/mobile-application",
      href: "/service/offend/mobile-application",
    },
    {
      id: "compromise-assessment",
      name: "Compromise Assessment",
      value: "/service/offend/compromise-assessment",
      href: "/service/offend/compromise-assessment",
    },
  ];
  // Determine the title based on the current path
  const getTitle = () => {
    if (pathname.includes("web-application")) return "Web Application";
    if (pathname.includes("mobile-application")) return "Mobile Application";
    if (pathname.includes("compromise-assessment")) return "Compromise Assessment";
    if (pathname.includes("adversary-simulation")) return "APT Adversary";
    if (pathname.includes("scenario-based")) return "Scenario-based Penetration Test";
    if (pathname.includes("exploit-pack")) return "Exploit Pack";
    return "Services";
  };

  const getCategory = () => {
    if (pathname.includes("/offend")) return "Offend";
    if (pathname.includes("/manage")) return "Manage";
    return null;
  };

  const category = getCategory();
  const isAttackAllAreas = ["adversary-simulation", "scenario-based"].some((value) =>
    pathname.includes(value)
  );
  const isAssessment = ["web-application", "mobile-application", "compromise-assessment"].some(
    (value) => pathname.includes(value)
  );
  // When the category has a third breadcrumb segment, "Offend" links back to /service.
  const hasSubCategory = isAttackAllAreas || isAssessment;

  return (
    <div className="relative min-h-screen flex flex-col bg-background pb-16 sm:pb-[120px]">
      {/* Scenario-based Penetration Test uses a distinct faceted low-poly hero
          background per Figma (node 71:2719 / Frame 1000005369). Opacity is
          baked into the SVG (each shard at 10%), so no opacity class here. */}
      {pathname.includes("scenario-based") && (
        <SubpageBackground
          src="/images/service/offend/scenario-based-penetration-test/hero-bg.svg"
          width={1440}
          height={860}
        />
      )}

      {/* Other Offend subpages use the honeycomb + circuit background per Figma
          (node 731:74976, e.g. APT Simulation), rendered at 20% opacity. */}
      {(pathname.includes("exploit-pack") ||
        pathname.includes("adversary-simulation") ||
        pathname.includes("mobile-application") ||
        pathname.includes("compromise-assessment") ||
        pathname.includes("attack-all-areas") ||
        pathname.includes("web-application")) && (
        <SubpageBackground
          src="/images/subpage-background-tech.png"
          width={1548}
          height={1027}
          className="opacity-20"
        />
      )}

      <div className="relative z-10 flex flex-col flex-1">
        {/* Breadcrumb */}
        {/* Header is fixed (h-72). Figma (node 694:69332) places the breadcrumb
            at y=128 → 72px header clearance + 56px gap. */}
        {category && (
          <div className="w-full px-4 sm:px-6 lg:px-8 pt-[112px] md:pt-[128px] overflow-hidden">
            <div className="relative max-w-[1300px] mx-auto">
              {/* Red accent line fills the left gutter (screen edge → title accent
                  bar); the parent's overflow-hidden clips the overhang. */}
              <span
                aria-hidden
                className="pointer-events-none absolute top-1/2 right-full -translate-y-1/2 h-[2px] w-screen bg-primary"
              />
              <nav className="flex flex-wrap items-center gap-1 border-l-[5px] border-transparent pl-4 md:pl-6 text-sm tracking-[0.35px] text-white">
                <Link href="/service" className="transition-colors hover:text-text-default">
                  Services
                </Link>
                <span>/</span>
                {hasSubCategory ? (
                  <Link href="/service" className="transition-colors hover:text-text-default">
                    {category}
                  </Link>
                ) : (
                  <span>{category}</span>
                )}
                {isAttackAllAreas && (
                  <>
                    <span>/</span>
                    <span>Attack All Areas</span>
                  </>
                )}
                {isAssessment && (
                  <>
                    <span>/</span>
                    <span>Assessment</span>
                  </>
                )}
              </nav>
            </div>
          </div>
        )}

        {/* Title and Tab Section - Added more spacing */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          {isAttackAllAreas && (
            <section className="w-full max-w-[1300px] mx-auto pb-6 pt-8 md:pb-[48px]">
              {/* mobileLines=2: "Scenario-based Penetration Test" wraps below `sm`. */}
              <PageTitle className="mb-4 md:mb-6" mobileLines={2}>
                {getTitle()}
              </PageTitle>

              {/* Tab Navigation - Added gap below */}
              <Tabs
                tabs={tabs}
                currentValue={pathname}
                style={TAB_STYLE.STYLE_1}
                showContent={true}
                useRouter={true}
                orientation={TAB_ORIENTATION.HORIZONTAL}
                customClassName="[&>a:first-child]:!pl-0"
              />
            </section>
          )}

          {["web-application", "mobile-application", "compromise-assessment"].some((value) =>
            pathname.includes(value)
          ) && (
            <section className="w-full max-w-[1300px] mx-auto pb-6 pt-8 md:pb-[48px]">
              {/* Web / Mobile / Compromise Assessment all fit on one line at
                  390px, so nothing needs reserving here. */}
              <PageTitle className="mb-4 md:mb-6">{getTitle()}</PageTitle>
              <Tabs
                tabs={tabsAssessment}
                currentValue={pathname}
                style={TAB_STYLE.STYLE_1}
                showContent={false}
                useRouter={true}
                orientation={TAB_ORIENTATION.HORIZONTAL}
                customClassName="[&>a:first-child]:!pl-0"
              />
            </section>
          )}

          {pathname.includes("exploit-pack") && (
            <section className="w-full max-w-[1300px] mx-auto pb-6 pt-8 md:pb-[48px]">
              {/* Only ever shows "Exploit Pack" — one line, and no tab row under
                  it to be pushed around anyway. No bottom margin either: that
                  24px exists to separate the title from the tab row, and with no
                  tabs here it just pushed the content 24px past the 56px every
                  other subpage puts under its header block. */}
              <PageTitle>{getTitle()}</PageTitle>
            </section>
          )}
        </div>

        {/* Page Content - Added top spacing for separation */}
        <main className="flex-1 w-full">{children}</main>
      </div>
    </div>
  );
}
